import styles from "./nudgeModals.module.scss";
import { useEffect, useState, useContext } from "react";
// redux
import { useSelector } from "react-redux";
import { UserData, userType as userTypeData } from "@src/@slice/userSlice";
// user
import { UserContext } from "@src/App";
// utils
import axios from "@src/api/axios";
import { isExternalUrl, HOSTNAME, BASE_URL } from "@src/utils";
import { ENCRYPTION_KEY, encryptAES, decryptAES } from "@src/utils";
// hooks
import useScrollLock from "@src/utils/hooks/useEnableScrollLock";
import { CHECK_SIGN_UP_COMPLETE_DATE } from "@src/utils/hooks/useInitUser";
// components
import { TooltipB, ButtonWrap, ButtonBox, Blank } from "@shared/src";
// ga
import { logEvent } from "@src/reactGa";

// Props
interface Props {
  onShow?: (state: boolean) => void;
}

/**
 * 정보 업데이트 모달(이용자 정보 업데이트 안내 모달)
 *
 * @param canBeShown 모달 표시 여부
 * @param setcanBeShown 모달 표시 여부 변경 함수
 */
const WeddinghallReminder = ({ onShow }: Props) => {
  // 키 이름
  const oldKey = `need-signup-additional`;
  const keyName = `h2mwbell:weddinghall-update`;

  // user 정보
  const user = useContext(UserContext);
  const userData = useSelector(UserData);
  const userType = useSelector(userTypeData);
  const { nick } = userData;

  // 모달 노출 여부
  const [isShow, setIsShow] = useState(false);

  // 노출 조건 충족 여부
  const [showCondition, setShowCondition] = useState(false);
  const { enableScrollLock, disableScrollLock } = useScrollLock();

  // 모달 노출 조건 체크(날짜가 맞는지+웨딩홀 입력 여부)
  const [isModalQualified, setIsModalQualified] = useState<{
    isQualified: boolean;
    nextCheckDt: string;
    target: boolean;
  } | null>(null);

  useEffect(() => {
    if (userType === "COMPLETE_AUTH_USER") {
      axios
        .get<{
          item: {
            nextCheckDt: string;
            target: boolean;
          };
        }>(BASE_URL + "/v4/20250407/customer/wedding-hall-input-target")
        .then((res) => {
          const nextCheckDt = res.data.item.nextCheckDt;
          const target = res.data.item.target;
          const now = new Date();
          const dateNextCheckDt = new Date(nextCheckDt);
          if (now === dateNextCheckDt && target) {
            // 이때만 노출
            setIsModalQualified({
              isQualified: true,
              nextCheckDt: nextCheckDt,
              target: target,
            });
          } else {
            setIsModalQualified({
              isQualified: false,
              nextCheckDt: nextCheckDt,
              target: target,
            });
          }
        });
    }
  }, [userType]);

  // 모달 노출
  useEffect(() => {
    if (showCondition) {
      setIsShow(true);
    } else {
      setIsShow(false);
    }
    // 기존 키들 삭제
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.includes(oldKey)) {
        localStorage.removeItem(key);
      }
    }
  }, [showCondition]);

  // 모달 노출 될 경우 스크롤 이동 + 락 처리
  useEffect(() => {
    if (isShow) {
      window.scrollTo(0, 0);
      enableScrollLock();
    } else {
      disableScrollLock();
    }
    onShow && onShow(isShow);
  }, [isShow]);

  // 비밀 키
  const secretKey = ENCRYPTION_KEY;

  const conditionCheckHandler = () => {
    // 보여질지 여부
    let isVisible = userType === "COMPLETE_AUTH_USER";

    // 로그인 안된 상태면 모달 안띄움
    if (!user?.me?.uuid) {
      isVisible = false;
    }

    if (!isModalQualified?.isQualified) {
      isVisible = false;
    }

    if (user?.me?.uuid) {
      // 기존 저장된 값 있는지 체크
      const lastShowInfoWithNewKey = localStorage.getItem(keyName);
      const decryptedData = lastShowInfoWithNewKey
        ? JSON.parse(decryptAES(lastShowInfoWithNewKey, secretKey))
        : null;
      const savedUuid = decryptedData ? decryptedData.payload : null;
      const savedFlag = decryptedData ? decryptedData.flag : null;

      // 이전에 저장된 값이 있으면 체크
      if (savedUuid === user?.me?.uuid) {
        // flag가 같으면 안띄움
        if (savedFlag === isModalQualified?.nextCheckDt) {
          isVisible = false;
        }
      } else {
        // 기존 값 삭제
        if (savedUuid) {
          localStorage.removeItem(keyName);
        }
      }
    }

    // 조건 설정
    setShowCondition(isVisible);
  };

  // 모달 표시 조건
  useEffect(() => {
    if (!user.me || isModalQualified === null) return;
    conditionCheckHandler();
  }, [user?.me, isModalQualified]);

  const { regTsp } = user?.me || {};
  const [content, setContent] = useState("");

  useEffect(() => {
    if (regTsp) {
      // 특정 날짜 기준으로 이전 가입자와 이후 가입자의 텍스트 내용이 다름
      const isAfterDate = new Date(regTsp) > CHECK_SIGN_UP_COMPLETE_DATE;
      const content = isAfterDate
        ? `웨딩홀을 정하셨다면,\n예식 정보를 업데이트 해보세요!🤗\n${nick}님에게 맞는 콘텐츠를 추천해드려요.`
        : `예식 정보를 업데이트 해보세요!🤗\n${nick}님에게 맞는 콘텐츠를 추천해드려요.`;
      setContent(content);
    }
  }, [regTsp]);

  // 키 저장
  const saveKey = () => {
    // 보안을 위해 로컬스토리지에 저장할 때 암호화
    const now = new Date().toString();
    const data = {
      date: now,
      payload: user?.me?.uuid ?? "",
      flag: isModalQualified?.nextCheckDt,
    };
    const original = JSON.stringify(data);
    const encrypted = encryptAES(original, secretKey);
    localStorage.setItem(keyName, encrypted);
  };

  // 모달 닫기 -> 로컬스토리지에 저장
  const onClose = () => {
    saveKey();
    setIsShow(false);
  };

  return (
    <div className={styles[`info-modal-wrap`]} style={{ display: isShow ? "block" : "none" }}>
      <div
        className={styles[`overlay`]}
        onClick={onClose}
        style={{ background: `rgba(255, 255, 255, 0.9)` }}
      ></div>
      <TooltipB
        direction="bottom"
        pointerLocation="left"
        close={onClose}
        zIndex={9999}
        marginForCustom="-10px 0 0 20px"
        widthValue={{ width: "auto", maxWidth: "400px", minWidth: "fit-content" }}
        extra={
          <div
            style={{
              margin: "0 -4px",
            }}
          >
            <p>{userData?.nick ? content : ""}</p>
            <Blank height={16} />
            <ButtonWrap noPaddingPosition="all" firstRatio={2} secondRatio={3}>
              <ButtonBox size="medium" theme={"gray50"} onClick={onClose} text="다음에" />
              <ButtonBox
                size="medium"
                theme={"red500"}
                onClick={() => {
                  onClose();
                  logEvent("Home_WriteProfile", {});
                  window.location.href = isExternalUrl(
                    `${HOSTNAME}/sign-additional/2?type=Home_WriteProfile_Complete`,
                  );
                }}
                text="업데이트 하러 가기"
              />
            </ButtonWrap>
          </div>
        }
      />
    </div>
  );
};

export default WeddinghallReminder;
